import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { TypeIcon, useCheckOff } from '../components/CheckOff';
import { NextStepList, type NextStepRow } from './Dashboard';
import { REMINDER_TYPE_LABEL, TrashButton, useDeleteReminder } from '../components/RemindersPanel';
import { ACTIVITY, ACTIVITY_TYPES } from '../lib/activity';
import { Button, Card, cx, Empty, ErrorBox, PageHeader, Pill, Select, Spinner } from '../components/ui';
import { addDaysISO, date, personName, todayISO } from '../lib/format';
import { supabase, unwrap } from '../lib/supabase';

export async function loadOpenReminders(until?: string) {
  let q = supabase
    .from('reminders')
    .select('id, due_date, note, reminder_type, activity_type, status, auto_generated, contacts(id, first_name, last_name, display_name), deals(id, name)')
    .neq('status', 'done')
    .order('due_date');
  if (until) q = q.lte('due_date', until);
  return unwrap(await q);
}
export type ReminderRow = Awaited<ReturnType<typeof loadOpenReminders>>[number];

export function ReminderList({ rows, compact }: { rows: ReminderRow[]; compact?: boolean }) {
  const qc = useQueryClient();
  const { checkOffReminder } = useCheckOff();
  const del = useDeleteReminder();
  const setType = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: string }) => unwrap(await supabase.from('reminders').update({ activity_type: type || null }).eq('id', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders'] }),
  });
  const snooze = useMutation({
    mutationFn: async ({ id, days }: { id: string; days: number }) => unwrap(await supabase.from('reminders').update({ due_date: addDaysISO(days), status: 'open' }).eq('id', id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['nav-counts'] });
    },
  });
  const today = todayISO();
  if (rows.length === 0) return <Empty>Nothing due. </Empty>;
  return (
    <>
    <ul className="divide-y divide-slate-100">
      {rows.map((r) => (
        <li key={r.id} className="group flex items-start gap-2.5 py-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            aria-label="Done"
            onChange={() =>
              checkOffReminder({ id: r.id, activity_type: r.activity_type, contactId: r.contacts?.id ?? null, dealId: r.deals?.id ?? null, name: r.contacts ? personName(r.contacts) : (r.deals?.name ?? 'this') })
            }
          />
          <TypeIcon type={r.activity_type} />
          <div className="min-w-0 flex-1">
            <div className="text-slate-800">{r.note}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              <span className={cx(r.due_date < today && 'font-medium text-rose-600', r.due_date === today && 'font-medium text-amber-700')}>
                {r.due_date < today ? `overdue · ${date(r.due_date)}` : r.due_date === today ? 'today' : date(r.due_date)}
              </span>
              <Pill>{REMINDER_TYPE_LABEL[r.reminder_type] ?? r.reminder_type}</Pill>
              {!r.activity_type && !compact && (
                <Select value="" onChange={(e) => setType.mutate({ id: r.id, type: e.target.value })} className="!w-auto !py-0 text-xs" aria-label="Set type">
                  <option value="">Set type…</option>
                  {ACTIVITY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {ACTIVITY[t].label}
                    </option>
                  ))}
                </Select>
              )}
              {r.contacts && (
                <Link to={`/contacts/${r.contacts.id}`} className="text-indigo-600 hover:underline">
                  {personName(r.contacts)}
                </Link>
              )}
              {r.deals && (
                <Link to={`/deals/${r.deals.id}`} className="text-indigo-600 hover:underline">
                  {r.deals.name}
                </Link>
              )}
            </div>
          </div>
          {!compact && (
            <div className="hidden gap-1 group-hover:flex">
              <Button size="sm" variant="ghost" onClick={() => snooze.mutate({ id: r.id, days: 1 })}>
                +1 day
              </Button>
              <Button size="sm" variant="ghost" onClick={() => snooze.mutate({ id: r.id, days: 7 })}>
                +1 week
              </Button>
            </div>
          )}
          <TrashButton onClick={() => del.mutate(r.id)} />
        </li>
      ))}
    </ul>
    </>
  );
}

export function Reminders() {
  const [range, setRange] = useState('14');
  const { data = [], isLoading, error } = useQuery({
    queryKey: ['reminders', 'all', range],
    queryFn: () => loadOpenReminders(range === 'all' ? undefined : addDaysISO(Number(range))),
  });
  const today = todayISO();
  const due = data.filter((r) => r.due_date <= today);
  const upcoming = data.filter((r) => r.due_date > today);
  return (
    <div>
      <PageHeader
        title="Reminders"
        subtitle="Add follow-ups from any contact or deal. Deal dates, payments and lease expirations create their own."
        actions={
          <Select value={range} onChange={(e) => setRange(e.target.value)} className="!w-44">
            <option value="7">Next 7 days</option>
            <option value="14">Next 2 weeks</option>
            <option value="30">Next 30 days</option>
            <option value="all">Everything open</option>
          </Select>
        }
      />
      <ErrorBox error={error} />
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title={`Due now (${due.length})`}>
            <ReminderList rows={due} />
          </Card>
          <Card title={`Coming up (${upcoming.length})`}>
            <ReminderList rows={upcoming} />
          </Card>
          <NextStepsCard />
        </div>
      )}
    </div>
  );
}

/** Every open deal's next step (Home shows a few; this is the full list). */
function NextStepsCard() {
  const { data = [] } = useQuery({
    queryKey: ['dashboard', 'all-next-steps'],
    queryFn: async () =>
      unwrap(
        await supabase
          .from('deals')
          .select('id, name, deal_type, stage, next_step, next_step_date, next_step_type, updated_at, contacts!deals_primary_contact_id_fkey(first_name, last_name, display_name)')
          .not('next_step', 'is', null)
          .not('stage', 'in', '(closed,closed_fully_paid)')
          .order('next_step_date', { nullsFirst: false }),
      ),
  });
  const { checkOffStep } = useCheckOff();
  return (
    <Card title={`Deal next steps (${data.length})`}>
      {data.length === 0 ? (
        <Empty>No open next steps.</Empty>
      ) : (
        <NextStepList
          rows={data as NextStepRow[]}
          divider={false}
          onDone={(d) => checkOffStep({ dealId: d.id, type: d.next_step_type, name: d.contacts ? personName(d.contacts) : d.name })}
        />
      )}
    </Card>
  );
}
